import { Box, Input, Button, Tooltip } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import Icons from '../../Icons';
import {
  SUPPLIED_VALUE_PLACEHOLDER,
  toDateInputValue,
  toIso8601,
  validateSuppliedValueConfig,
  sampleExpiryDate,
} from 'helpers/suppliedValue';

const labelStyle = {
  fontSize: '14px',
  paddingBottom: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const inputStyle = { width: '270px' };

function SuppliedValueMenu({ keyObj, updateMetadata, closeMenu }) {
  const [groupId, setGroupId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [ttlDays, setTtlDays] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [exportKey, setExportKey] = useState('');

  useEffect(() => {
    const metadata = keyObj?.object?.metadata || {};
    setGroupId(metadata.company_group_id || '');
    setStart(toDateInputValue(metadata.start));
    setEnd(toDateInputValue(metadata.end));
    setTtlDays(
      metadata.ttl_days === undefined || metadata.ttl_days === null
        ? ''
        : String(metadata.ttl_days)
    );
    setDisplayValue(metadata.display_value || '');
    setExportKey(metadata.export_key || '');
  }, [keyObj]);

  const errors = validateSuppliedValueConfig({
    company_group_id: groupId,
    start,
    end,
    ttl_days: ttlDays,
    display_value: displayValue,
    export_key: exportKey,
  });

  // the var itself no longer renders a date, so show what the consuming var
  // would print. Counted from today, as the server's preview is.
  const expirySample = sampleExpiryDate(ttlDays);

  const handleApplyClick = () => {
    updateMetadata('company_group_id', groupId.trim());
    // start of day for the lower bound, end of day for the upper, so an
    // inclusive date range in the UI matches the backend's datetime window.
    updateMetadata('start', toIso8601(start, 'start'));
    updateMetadata('end', toIso8601(end, 'end'));
    updateMetadata(
      'ttl_days',
      ttlDays.trim() === '' ? undefined : parseInt(ttlDays, 10)
    );
    // blank clears the key, so the var falls back to the generic placeholder
    // rather than rendering an empty string.
    updateMetadata('display_value', displayValue.trim() || undefined);
    updateMetadata('export_key', exportKey.trim() || undefined);

    // the canvas text is derived from display_value and ttl_days, so it has to
    // be redrawn once the metadata above has landed.
    keyObj?.object?.refreshDisplayText?.();

    closeMenu();
  };

  return (
    <Box>
      <Box
        sx={{
          fontWeight: 700,
          fontSize: '14px',
          paddingBottom: '1rem',
        }}
      >
        Promo Code
      </Box>

      <Box>
        <Box sx={labelStyle}>
          <Box>Code group</Box>
          <Tooltip label="The group of codes to draw from. A code is allocated from this group when the ad is produced, and the same order always keeps the code it was first given.">
            <Box color="#666666" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          fontSize="14px"
          borderRadius={0}
          style={inputStyle}
          value={groupId}
          name="companyGroupId"
          onChange={e => setGroupId(e.target.value)}
        />
        {errors.company_group_id && (
          <Box sx={{ fontSize: '12px', color: '#e53e3e', paddingTop: '4px' }}>
            {errors.company_group_id}
          </Box>
        )}
      </Box>

      <Box sx={{ marginTop: '1rem' }}>
        <Box sx={labelStyle}>
          <Box>Sample code</Box>
          <Tooltip label="Optional. A representative code shown on the canvas and in previews so you can check how a real one fits. It is never given to a customer. Leave blank to show a generic placeholder.">
            <Box color="#666666" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          fontSize="14px"
          borderRadius={0}
          style={inputStyle}
          value={displayValue}
          name="displayValue"
          placeholder={SUPPLIED_VALUE_PLACEHOLDER}
          onChange={e => setDisplayValue(e.target.value)}
        />
        {errors.display_value && (
          <Box sx={{ fontSize: '12px', color: '#e53e3e', paddingTop: '4px' }}>
            {errors.display_value}
          </Box>
        )}
      </Box>

      <Box sx={{ marginTop: '1rem' }}>
        <Box sx={labelStyle}>
          <Box>Start date</Box>
          <Tooltip label="Optional. Codes are only allocated on or after this date. Leave blank for no start date.">
            <Box color="#666666" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          type="date"
          fontSize="14px"
          borderRadius={0}
          style={inputStyle}
          value={start}
          name="startDate"
          onChange={e => setStart(e.target.value)}
        />
      </Box>

      <Box sx={{ marginTop: '1rem' }}>
        <Box sx={labelStyle}>
          <Box>End date</Box>
          <Tooltip label="Optional. Codes stop being allocated after this date. Orders that already have a code keep showing it. Leave blank for no end date.">
            <Box color="#666666" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          type="date"
          fontSize="14px"
          borderRadius={0}
          style={inputStyle}
          value={end}
          name="endDate"
          onChange={e => setEnd(e.target.value)}
        />
        {errors.end && (
          <Box sx={{ fontSize: '12px', color: '#e53e3e', paddingTop: '4px' }}>
            {errors.end}
          </Box>
        )}
      </Box>

      <Box sx={{ marginTop: '1rem' }}>
        <Box sx={labelStyle}>
          <Box>Expires after (days)</Box>
          <Tooltip label="Optional. How long the code stays valid, counted from the day it is allocated. On its own this prints nothing — name a field below to show the date. Leave blank if the code never expires.">
            <Box color="#666666" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          fontSize="14px"
          borderRadius={0}
          style={inputStyle}
          value={ttlDays}
          name="ttlDays"
          onChange={e => setTtlDays(e.target.value)}
        />
        {errors.ttl_days && (
          <Box sx={{ fontSize: '12px', color: '#e53e3e', paddingTop: '4px' }}>
            {errors.ttl_days}
          </Box>
        )}
        {!errors.ttl_days && expirySample && (
          <Box sx={{ fontSize: '12px', color: '#666666', paddingTop: '4px' }}>
            A code issued today would expire {expirySample}.
          </Box>
        )}
      </Box>

      <Box sx={{ marginTop: '1rem' }}>
        <Box sx={labelStyle}>
          <Box>Expiry date field name</Box>
          <Tooltip label="Optional. Names a field that carries the expiry date, so you can place and word it yourself. Enter this same name in a text field's {{ }} expression to show the date there. Requires an expiry above.">
            <Box color="#666666" cursor="pointer">
              <Icons.Info size={22} />
            </Box>
          </Tooltip>
        </Box>
        <Input
          fontSize="14px"
          borderRadius={0}
          style={inputStyle}
          value={exportKey}
          name="exportKey"
          placeholder="promo_exp"
          onChange={e => setExportKey(e.target.value)}
        />
        {errors.export_key ? (
          <Box sx={{ fontSize: '12px', color: '#e53e3e', paddingTop: '4px' }}>
            {errors.export_key}
          </Box>
        ) : (
          <Box sx={{ fontSize: '12px', color: '#666666', paddingTop: '4px' }}>
            {exportKey.trim()
              ? `Add {{${exportKey.trim()}}} to a text field to show the date.`
              : "Enter this same name in a text field's {{ }} expression to show the expiry date."}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: '0.75rem', paddingTop: '1rem' }}>
        <Button
          colorScheme="orange"
          backgroundColor="rgb(255, 111, 15)"
          borderRadius={0}
          name="suppliedValueApply"
          size="sm"
          flex={1}
          disabled={Object.keys(errors).length > 0}
          onClick={handleApplyClick}
        >
          Apply
        </Button>
        <Button borderRadius={0} size="sm" flex={1} onClick={closeMenu}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
export default SuppliedValueMenu;
